import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { ConfirmButton } from 'components/common';

import type { CalendarApi } from '@fullcalendar/core';
import type { CalendarEventExtended } from 'shared-types';

interface Props {
  calendarApi?: CalendarApi;
  event: CalendarEventExtended;
  setEvent?: React.Dispatch<React.SetStateAction<CalendarEventExtended>>;
}

const CalendarEventParticipantRemoveSelf = ({
  calendarApi,
  event,
  setEvent
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, theme, user } = useReduxState();

  const router = useRouter();

  const handleConfirm = async () => {
    try {
      await api.delete(
        `/admin/calendar-events/${event.id}/registration/${user?.id}`
      );

      event.numberOfParticipantsAdded =
        event.userStatus === 'added'
          ? event.numberOfParticipantsAdded - 1
          : event.numberOfParticipantsAdded;
      if (
        event.participationLimit &&
        event.numberOfParticipantsAdded >= event.participationLimit
      ) {
        event.isFull = true;
      } else event.isFull = false;

      event.numberOfParticipantsQueued =
        event.userStatus === 'queued'
          ? event.numberOfParticipantsQueued - 1
          : event.numberOfParticipantsQueued;

      event.userStatus = null;
      event.reqUserCustomAttributes = null;

      if (calendarApi) {
        const calendarEvent = calendarApi.getEventById(`${event.id}`);
        calendarEvent?.setProp('color', theme.lightGreyDark);
        calendarEvent?.setExtendedProp('accessRequestId', null);
        calendarEvent?.setExtendedProp('icon', null);
        calendarEvent?.setExtendedProp(
          'numberOfParticipantsAdded',
          event.numberOfParticipantsAdded
        );
        calendarEvent?.setExtendedProp(
          'numberOfParticipantsQueued',
          event.numberOfParticipantsQueued
        );
        calendarEvent?.setExtendedProp('reqUserCustomAttributes', null);
        calendarEvent?.setExtendedProp('userStatus', null);
      }
      if (setEvent) setEvent({ ...event, userStatus: null });

      if (!calendarApi && !setEvent) router.push('/calendar');
      else dispatch(actions.updatePageState({ calendarEvent: event }));

      return true;
    } catch (err) {
      return false;
    }
  };

  if (!event || !event.allowDeregistration) return null;

  return (
    <ConfirmButton
      actionText={messages.commonConfirm}
      buttonCategory="lighter"
      buttonIcon="trash-alt"
      buttonIsRounded={true}
      buttonSize="medium"
      buttonStyle={{ marginRight: 5 }}
      buttonText={messages.eventCalendarParticipantLeaveEvent}
      category="danger"
      confirmText={messages.eventCalendarParticipantLeaveEventConfirm}
      confirmTitle={messages.eventCalendarParticipantLeaveEvent}
      onConfirm={handleConfirm}
      successMessage={messages.eventCalendarParticipantLeaveEventSuccess}
      {...(!event.allowDeregistration
        ? {
            buttonTooltip:
              messages.eventCalendarEventParticipationDeregisterNotAllowed,
            disabled: true
          }
        : {})}
    />
  );
};

export default CalendarEventParticipantRemoveSelf;
